import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Cards from "../../ReusableComponents/Cards/Cards";
import SearchBar from "../../ReusableComponents/Search/SearchBar";
import CustomCard from "../../ReusableComponents/CustomCard/CustomCard";
import CustomSnackbar from "../../components/CustomSnackbar";
import api from "../../api/api";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import WarrantyDetails from "./WarrantyDetails";
import DownloadIcon from "@mui/icons-material/Download";
import { DownloadCSV } from "../../ReusableComponents/DownloadCSV";
import en_ja from "../../translations/en_ja";
import CustomTableServer from "../../ReusableComponents/Table/CustomTableServer";

export default function WarrantyManagement() {
    const [searchTerm, setSearchTerm] = useState("");
    const [warranties, setWarranties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cardsData, setCardsData] = useState({});

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsWarranty, setDetailsWarranty] = useState(null);

    // track action in-progress per id
    const [actionLoadingIds, setActionLoadingIds] = useState(new Set());

    // confirm dialog states
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmRow, setConfirmRow] = useState(null);

    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);

    const cleanUrl = (u) => {
        if (!u) return "";
        return String(u).replace(/"%22/g, "").replace(/%22/g, "").replace(/"/g, "");
    };

    const mapApiItemToRow = (item) => {
        const { product, user } = item;

        return {
            id: item.id,
            status: item.status || "-",
            user_name: user?.name || "-",
            birth_date: user?.birth_date || "-",
            gender:
                user?.gender === "0" || user?.gender === 0
                    ? en_ja["Male"]
                    : user?.gender === "1" || user?.gender === 1
                        ? en_ja["Female"]
                        : "-",
            product_name: product?.title || "-",
            product_code: product?.product_code || "-",
            purchase_store: item?.purchase_store || "-",
            product_price: product?.product_price || "-",
            date_registered: item.created_at || "-",
            purchase_date: item.purchase_date || "-",
            expiry_date: item.expiry_date || "-",
            email: user?.email || "-",
            phone: user?.phone_number || "-",
            purchase_receipt_url: cleanUrl(item.purchase_receipt),
            pin: user?.pin || "-",
            raw: item,
        };
    };


    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, "0")}/${String(
            d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
    };

    const formatDOB = (dob) => {
        if (!dob || dob.length !== 8) return dob;

        const year = dob.slice(0, 4);
        const month = dob.slice(4, 6);
        const day = dob.slice(6, 8);

        return `${day}/${month}/${year}`;
    };

    const filteredData = warranties.filter((row) => {
        const term = String(searchTerm || "").toLowerCase();
        if (!term) return true;

        const formattedDate = formatDate(row.date_registered || "").toLowerCase();

        return (
            row.user_name?.toLowerCase().includes(term) ||
            row.product_name?.toLowerCase().includes(term) ||
            row.product_code?.toLowerCase().includes(term) ||
            row.email?.toLowerCase().includes(term) ||
            formattedDate.includes(term) ||
            String(row.status || "").toLowerCase().includes(term)
        );
    });

    const handleRowClick = async (row) => {
        if (!row?.id) return;

        setLoading(true);
        try {
            const res = await api.get(`/v1/admin/product/warranty/${row.id}`);

            const body = res?.data?.data || res?.data || {};

            const merged = {
                ...(row.raw || {}),
                ...(typeof body === "object" ? body : {}),
            };

            const mapped = mapApiItemToRow(merged);

            setDetailsWarranty(mapped);
            setDetailsOpen(true);
        } catch (err) {
            console.error("Error fetching single warranty:", err);
            setSnackbarType("error");
            setSnackbarMessage("Failed to fetch warranty details");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const updateLocalWarrantyStatus = (id, newStatus) => {
        setWarranties((prev) => prev.map((w) => (w.id === id ? { ...w, status: newStatus } : w)));
        // if details popup is open for this id, update it too
        setDetailsWarranty((prev) => (prev?.id === id ? { ...prev, status: newStatus } : prev));
    };

    const setActionLoading = (id, isLoading) => {
        setActionLoadingIds((prev) => {
            const copy = new Set(prev);
            if (isLoading) copy.add(id);
            else copy.delete(id);
            return copy;
        });
    };

    const openConfirmDialog = (row, action) => {
        setConfirmRow(row);
        setConfirmAction(action);
        setConfirmOpen(true);
    };

    const closeConfirmDialog = () => {
        setConfirmOpen(false);
        setConfirmRow(null);
        setConfirmAction(null);
    };

    const sendStatusUpdateToApi = async (id, action) => {
        const payload = {
            warrantyId: id,
            status: action === "approve" ? "approved" : "rejected",
        };

        return api.post("/v1/admin/warranty/status", payload);
    };

    const handleConfirmAction = async () => {
        if (!confirmRow?.id || !confirmAction) return;
        const id = confirmRow.id;
        setActionLoading(id, true);
        try {
            await sendStatusUpdateToApi(id, confirmAction);

            const newStatus = confirmAction === "approve" ? "Approved" : "Rejected";
            updateLocalWarrantyStatus(id, newStatus);

            setSnackbarType("success");
            setSnackbarMessage(
                confirmAction === "approve" ? en_ja["Warranty approved"] || "Warranty approved"
                    : en_ja["Warranty rejected"] || "Warranty rejected"
            );
            setSnackbarOpen(true);

            closeConfirmDialog();

        } catch (err) {
            console.error("Error updating warranty status:", err);
            setSnackbarType("error");
            setSnackbarMessage("Failed to change warranty status");
            setSnackbarOpen(true);
        } finally {
            setActionLoading(id, false);
        }
    };

    const handleChangeWarrantyStatus = async (id, action) => {
        // keep existing method for compatibility if you want direct call without dialog
        openConfirmDialog(warranties.find((w) => w.id === id), action);
    };

    const getWarrantyDetailsList = async (pageNumber = 1) => {
        setLoading(true);
        try {
            const res = await api.get(`/v1/admin/waranty-stats?page=${pageNumber}`);

            const rawList = Array.isArray(res?.data?.data) ? res.data.data : [];
            const mappedList = rawList.map(mapApiItemToRow);

            setWarranties(mappedList);

            const totalWarranties =
                res?.data?.WarrantyCardData?.totalWarranties ?? 0;

            setTotalCount(totalWarranties);

            // IMPORTANT: store the full card object
            setCardsData(res?.data?.WarrantyCardData || {});

        } catch (err) {
            console.error("Error fetching warranty details:", err);
            setSnackbarType("error");
            setSnackbarMessage("Failed to fetch warranties");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadWarrantyFile = async () => {
        try {
            const res = await api.get("/v1/admin/product/warranties/download");

            const backendRows = Array.isArray(res?.data?.data)
                ? res?.data?.data
                : Array.isArray(res?.data)
                    ? res.data
                    : [];

            if (backendRows.length === 0) {
                setSnackbarType("warning");
                setSnackbarMessage("No warranty data available to download");
                setSnackbarOpen(true);
                return;
            }

            const mappedRows = backendRows?.map(mapApiItemToRow);

            const csvColumns = [
                { id: "user_name", label: en_ja["User Name"] },
                { id: "birth_date", label: en_ja["Date of Birth"] },
                { id: "gender", label: en_ja["Gender"] },
                { id: "product_name", label: en_ja["Product Name"] },
                { id: "product_code", label: en_ja["Product Code"] },
                { id: "purchase_store", label: en_ja["Purchase Store"] },
                { id: "email", label: en_ja["Email"] },
                { id: "date_registered", label: en_ja["Date Registered"] },
                { id: "status", label: en_ja["Status"] },
                { id: "pin", label: en_ja["PIN"] },
            ];

            const csvRows = mappedRows.map((r) => ({
                user_name: r.user_name,
                birth_date: formatDOB(r.birth_date),
                gender: r.gender,
                product_name: r.product_name,
                product_code: r.product_code,
                purchase_store: r.purchase_store,
                email: r.email,
                date_registered: formatDate(r.date_registered),
                status: r.status,
                pin: r.pin
            }));

            DownloadCSV(csvColumns, csvRows, `${en_ja["Warranty Report"] || "Warranty_Report"}`);

        } catch (error) {
            console.error("Download failed:", error);
            setSnackbarType("error");
            setSnackbarMessage("Failed to download warranty report");
            setSnackbarOpen(true);
        }
    };

    const columns = [
        {
            id: "user_name",
            label: en_ja["User Name"],
            sortable: true,
            render: (row) => <Box onClick={() => handleRowClick(row)} sx={{ cursor: "pointer" }}>{row.user_name}</Box>,
        },
        {
            id: "product_name",
            label: en_ja["Product Name"],
            sortable: true,
            render: (row) => (
                <Box onClick={() => handleRowClick(row)} sx={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.product_name}
                </Box>
            ),
        },
        {
            id: "product_code",
            label: en_ja["Product Code"],
            sortable: true,
            render: (row) => (
                <Box onClick={() => handleRowClick(row)} sx={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.product_code}
                </Box>
            ),
        },
        {
            id: "email",
            label: en_ja["Email"],
            sortable: true,
            render: (row) => (
                <Box onClick={() => handleRowClick(row)} sx={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.email}
                </Box>
            ),
        },

        // Status column
        {
            id: "status",
            label: en_ja["Status"] || "Status",
            sortable: true,
            render: (row) => {
                const status = (row.status || "").toLowerCase();

                let bgColor = status.includes("approved")
                    ? "#198754" // green
                    : status.includes("rejected")
                        ? "#dc3545" // red
                        : status.includes("pending")
                            ? "#fd7e14" // orange
                            : "#6c757d"; // gray

                return (
                    <Chip
                        label={row.status || "Pending"}
                        size="small"
                        sx={{
                            backgroundColor: bgColor,
                            color: "white",
                            fontWeight: 600,
                            fontSize: "10px",
                            textTransform: "uppercase",
                        }}
                    />
                );
            },
        },

        {
            id: "date_registered",
            label: en_ja["Date Registered"],
            sortable: true,
            render: (row) => <Box onClick={() => handleRowClick(row)} sx={{ cursor: "pointer" }}>{formatDate(row.date_registered)}</Box>,
        },

        {
            id: "actions",
            label: en_ja["Actions"] || "Actions",
            sortable: false,
            render: (row) => {
                const isApproved = row.status?.toLowerCase().includes("approved");
                const isRejected = row.status?.toLowerCase().includes("rejected");
                const loading = actionLoadingIds.has(row.id);

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* APPROVE */}
                        <Button
                            size="small"
                            variant="contained"
                            sx={{
                                backgroundColor: "#28a745",
                                color: "white",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "11px",
                                boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)",
                                // borderRadius: 3,
                                "&:hover": {
                                    backgroundColor: "#218838",
                                    boxShadow: "0 4px 12px rgba(40, 167, 69, 0.4)",
                                    transform: "translateY(-1px)",
                                },
                            }}
                            disabled={isApproved || loading}
                            onClick={() => handleChangeWarrantyStatus(row.id, "approve")}
                        >
                            {loading ? "..." : en_ja["Approve"] || "Approve"}
                        </Button>

                        {/* REJECT */}
                        <Button
                            size="small"
                            variant="contained"
                            sx={{
                                backgroundColor: "#dc3545",
                                color: "white",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "11px",
                                boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
                                // borderRadius: 3,
                                "&:hover": {
                                    backgroundColor: "#c82333",
                                    boxShadow: "0 4px 12px rgba(220, 53, 69, 0.4)",
                                    transform: "translateY(-1px)",
                                },
                            }}
                            disabled={isRejected || loading}
                            onClick={() => handleChangeWarrantyStatus(row.id, "reject")}
                        >
                            {loading ? "..." : en_ja["Reject"] || "Reject"}
                        </Button>
                    </Box>
                );
            },
        },
    ];

    // Direct action from details popup (no confirmation)
    const handleDirectAction = async (id, action) => {
        if (!id || !action) return;
        setActionLoading(id, true);

        try {
            await sendStatusUpdateToApi(id, action); // use your existing API function

            const newStatus = action === "approve" ? "Approved" : "Rejected";

            // update the local table rows
            updateLocalWarrantyStatus(id, newStatus);

            // update currently opened details (so it shows updated status briefly)
            setDetailsWarranty((prev) => (prev?.id === id ? { ...prev, status: newStatus } : prev));

            // show snackbar success
            setSnackbarType("success");
            setSnackbarMessage(
                action === "approve"
                    ? en_ja["Warranty approved"] || "Warranty approved"
                    : en_ja["Warranty rejected"] || "Warranty rejected"
            );
            setSnackbarOpen(true);

            // close details popup and clear detailsWarranty
            setDetailsOpen(false);
            setDetailsWarranty(null);

            // optional: refresh entire list/stats after action
            // await getWarrantyDetailsList();
        } catch (err) {
            console.error("Error updating warranty status:", err);
            setSnackbarType("error");
            setSnackbarMessage("Failed to change warranty status");
            setSnackbarOpen(true);
        } finally {
            setActionLoading(id, false);
        }
    };

    useEffect(() => {
        getWarrantyDetailsList(page + 1);
    }, [page]);

    return (
        <Box sx={{ mt: 2 }}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={() => setSnackbarOpen(false)}
            />

            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    width: "100%",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    maxWidth: "400px",
                }}
            >
                <Cards
                    title={en_ja["Total Warranties"]}
                    value={cardsData?.totalWarranties ?? 0}
                    icon={<VerifiedOutlinedIcon sx={{ color: "white", fontSize: 24 }} />}
                />
            </Box>

            <CustomCard
                boxSx={{ flexDirection: "row", justifyContent: "space-between", flexWrap: "wrap" }}
                innerBoxSx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                }}
            >
                <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadWarrantyFile}
                    disabled={loading || filteredData.length === 0}
                >
                    {en_ja["Warranty Report"] || "Warranty Report"}
                </Button>
            </CustomCard>

            <CustomTableServer
                columns={columns}
                rows={warranties}
                loading={loading}
                searchTerm={searchTerm}
                page={page}
                rowsPerPage={100}
                totalCount={totalCount}
                onPageChange={(e, newPage) => setPage(newPage)}
            />


            <WarrantyDetails
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                warrantyData={detailsWarranty}
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}

                onApprove={() => handleDirectAction(detailsWarranty?.id, "approve")}
                onReject={() => handleDirectAction(detailsWarranty?.id, "reject")}
                actionLoading={detailsWarranty ? actionLoadingIds.has(detailsWarranty.id) : false}
            />

            <Dialog
                open={confirmOpen}
                onClose={closeConfirmDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    <Typography variant="h6" textAlign="center" sx={{ flexGrow: 1 }}>
                        {confirmAction === "approve"
                            ? en_ja["Approve Warranty"] || "Approve Warranty"
                            : en_ja["Reject Warranty"] || "Reject Warranty"}
                    </Typography>

                    <IconButton onClick={closeConfirmDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ textAlign: "center", pt: 1, pb: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {confirmAction === "approve"
                            ? `${en_ja["Are you sure you want to approve"] || "Are you sure you want to approve"}`
                            : `${en_ja["Are you sure you want to reject"] || "Are you sure you want to reject"}`}{" "}
                        <strong>{confirmRow?.user_name}</strong>
                        {en_ja["'s warranty?"] || "'s warranty?"}
                    </Typography>

                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={closeConfirmDialog}
                        sx={{ textTransform: "none" }}
                    >
                        {en_ja["Cancel"] || "Cancel"}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleConfirmAction}
                        color={confirmAction === "approve" ? "success" : "error"}
                        sx={{ textTransform: "none" }}
                        disabled={confirmRow ? actionLoadingIds.has(confirmRow.id) : false}
                    >
                        {confirmRow && actionLoadingIds.has(confirmRow.id)
                            ? "..."
                            : confirmAction === "approve"
                                ? en_ja["Yes, Approve"] || "Yes, Approve"
                                : en_ja["Yes, Reject"] || "Yes, Reject"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
